import { useImportProgress, type ImportJob } from "@/contexts/ImportProgressContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Loader2, Check, X, Download, Youtube, FileText, 
  FolderInput, Search, Trash2 
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { safeDate } from "@/lib/safeDate";

const typeIcons = {
  url: FileText,
  youtube: Youtube,
  document: FileText,
  research: Search,
  "google-drive": FolderInput,
};

const typeLabels = {
  url: "Web Article",
  youtube: "YouTube Video",
  document: "Document",
  research: "Research",
  "google-drive": "Google Drive",
};

const statusLabels = {
  pending: "Waiting...",
  processing: "Processing...",
  extracting: "Extracting content...",
  analyzing: "Analyzing with AI...",
  completed: "Complete",
  failed: "Failed",
};

function JobItem({ job, onRemove }: { job: ImportJob; onRemove: () => void }) {
  const Icon = typeIcons[job.type];
  const isActive = job.status !== "completed" && job.status !== "failed";

  return (
    <div 
      className="p-3 border rounded-md space-y-2"
      data-testid={`import-job-${job.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {isActive ? (
            <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
          ) : job.status === "completed" ? (
            <Check className="h-4 w-4 text-green-500 shrink-0" />
          ) : (
            <X className="h-4 w-4 text-destructive shrink-0" />
          )}
          <div className="flex items-center gap-1.5 min-w-0">
            <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-sm font-medium truncate">{job.title}</span>
          </div>
        </div>
        {!isActive && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={onRemove}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">
            {statusLabels[job.status]}
          </span>
          <Badge variant="outline" className="text-xs">
            {typeLabels[job.type]}
          </Badge>
        </div>
        {isActive && (
          <Progress value={job.progress} className="h-1.5" />
        )}
        {job.error && (
          <p className="text-xs text-destructive">{job.error}</p>
        )}
        <p className="text-xs text-muted-foreground">
          {(() => {
            const started = safeDate(job.startedAt);
            return started ? formatDistanceToNow(started, { addSuffix: true }) : "";
          })()}
        </p>
      </div>
    </div>
  );
}

export function ImportProgressIndicator() {
  const { jobs, activeJobs, removeJob, clearCompleted } = useImportProgress();

  if (jobs.length === 0) {
    return null;
  }

  const hasCompleted = jobs.some(
    (job) => job.status === "completed" || job.status === "failed"
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="relative gap-2"
          data-testid="button-import-progress"
        >
          {activeJobs.length > 0 ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="hidden sm:inline">Importing...</span>
              <Badge variant="secondary" className="ml-1">
                {activeJobs.length}
              </Badge>
            </>
          ) : (
            <>
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Imports</span>
              <Badge variant="secondary" className="ml-1">
                {jobs.length}
              </Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b flex items-center justify-between">
          <h4 className="font-medium text-sm">Import Progress</h4>
          {hasCompleted && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearCompleted}
              className="text-xs h-7"
            >
              Clear completed
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-80">
          <div className="p-3 space-y-2">
            {jobs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No imports in progress
              </p>
            ) : (
              jobs.map((job) => (
                <JobItem
                  key={job.id}
                  job={job}
                  onRemove={() => removeJob(job.id)}
                />
              ))
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
